'use client';

import React, { useEffect, useRef } from 'react';

interface Benefit {
    icon: string;
    title: string;
    desc: string;
    color: string;
    bgColor: string;
    borderColor: string;
    highlight?: string;
}

const benefits: Benefit[] = [
    {
        icon: '🔴',
        title: 'Every class is live',
        desc: 'No pre-recorded videos. Every session on Wisly is a live class with a real instructor. You show up, ask questions, and learn in real time.',
        color: 'text-red-500',
        bgColor: 'bg-red-500/8',
        borderColor: 'border-red-500/20',
        highlight: 'No recordings, ever.',
    },
    {
        icon: '📅',
        title: 'Book sessions that fit your schedule',
        desc: 'Instructors set their available time slots. You browse, pick a time that works for you, and book your live session — it\'s that simple.',
        color: 'text-primary',
        bgColor: 'bg-primary/8',
        borderColor: 'border-primary/20',
        highlight: 'You pick the time.',
    },
    {
        icon: '🚀',
        title: 'Join structured bootcamps',
        desc: 'Want a more intensive experience? Join a bootcamp with a fixed timetable. Pay to enrol and attend live classes on the days your instructor runs them.',
        color: 'text-orange',
        bgColor: 'bg-orange/8',
        borderColor: 'border-orange/20',
        highlight: 'Structured & intensive.',
    },
    {
        icon: '🏫',
        title: 'Learn from institutions',
        desc: 'Enrol in structured programs from schools, vocational centres, and academies. SS classes, medical training, carpentry — all delivered live.',
        color: 'text-purple',
        bgColor: 'bg-purple/8',
        borderColor: 'border-purple/20',
        highlight: 'Real institutions.',
    },
    {
        icon: '🤝',
        title: 'Real interaction with instructors',
        desc: 'Ask questions during class, get instant feedback, and build a real relationship with your instructor. Learning is a two-way conversation.',
        color: 'text-green',
        bgColor: 'bg-green/8',
        borderColor: 'border-green/20',
        highlight: 'Two-way learning.',
    },
    {
        icon: '🌟',
        title: 'Track your growth',
        desc: 'See your session history, track your progress across subjects, and celebrate milestones as you grow. Your learning journey, visualised.',
        color: 'text-orange',
        bgColor: 'bg-orange/8',
        borderColor: 'border-orange/20',
        highlight: 'Your journey, tracked.',
    },
    {
        icon: '📱',
        title: 'Learn anything, anywhere',
        desc: 'Join live sessions from your phone, tablet, or computer. Whether you\'re at home, in a café, or on the go — your classroom is wherever you are.',
        color: 'text-primary',
        bgColor: 'bg-primary/8',
        borderColor: 'border-primary/20',
        highlight: 'Learn on any device.',
    },
    {
        icon: '💳',
        title: 'Pay only for what you attend',
        desc: 'Book individual live sessions and pay per session, or enrol in a bootcamp or program. No subscriptions, no hidden fees — just pay for what you need.',
        color: 'text-green',
        bgColor: 'bg-green/8',
        borderColor: 'border-green/20',
        highlight: 'Transparent pricing.',
    },
];

export default function LearnBenefitsSection() {
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) entry.target.classList.add('visible');
                });
            },
            { threshold: 0.08 }
        );
        const els = sectionRef.current?.querySelectorAll('.reveal');
        els?.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className="relative py-20 md:py-28 bg-foreground overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[700px] h-[700px] blob-primary opacity-20 translate-x-1/3 -translate-y-1/4" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] blob-accent opacity-15 -translate-x-1/4 translate-y-1/4" />
                <div
                    className="absolute inset-0 opacity-5"
                    style={{
                        backgroundImage: 'linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)',
                        backgroundSize: '60px 60px',
                    }}
                />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
                {/* Header */}
                <div className="max-w-3xl mb-14 md:mb-20">
                    <div className="reveal mb-5">
                        <span className="pill-tag border border-white/20 text-white/70 bg-white/5 text-xs uppercase tracking-wider">
                            What you gain
                        </span>
                    </div>
                    <h2 className="reveal text-section-title font-extrabold text-white mb-5 delay-100">
                        Everything you stand to gain as a learner.
                    </h2>
                    <p className="reveal text-lg text-white/60 font-body leading-relaxed max-w-2xl delay-200">
                        Wisly Learn is built entirely around you — the student. Here’s exactly what you get when you join.
                    </p>
                </div>

                {/* Benefits grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                    {benefits.map((benefit, i) => (
                        <div
                            key={benefit.title}
                            className="reveal card-hover cursor-pointer"
                            style={{ transitionDelay: `${i * 70}ms` }}
                        >
                            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 h-full group hover:bg-white/8 hover:border-white/20 transition-all duration-300 flex flex-col">
                                <div className={`w-12 h-12 ${benefit.bgColor} border ${benefit.borderColor} rounded-xl flex items-center justify-center text-2xl mb-4 flex-shrink-0`}>
                                    {benefit.icon}
                                </div>
                                <h3 className="font-bold text-white text-base mb-2">{benefit.title}</h3>
                                <p className="text-white/55 text-sm font-body leading-relaxed flex-1">{benefit.desc}</p>
                                {benefit.highlight && (
                                    <div className="mt-4 pt-4 border-t border-white/10">
                                        <span className={`text-xs font-bold ${benefit.color}`}>{benefit.highlight}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom callout */}
                <div className="reveal delay-400 mt-12">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
                        <div className="w-14 h-14 bg-primary/20 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0">
                            🎯
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold text-white text-lg mb-1">The Wisly promise to learners</h3>
                            <p className="text-white/60 text-sm font-body leading-relaxed">
                                Every class is live. Every instructor is real. Every session is scheduled. You will never watch a recording on Wisly — you will always be in a live room with a real person who is there to teach you.
                            </p>
                        </div>
                        <div className="flex-shrink-0">
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 text-primary text-sm font-semibold">
                                <span className="w-2 h-2 rounded-full bg-primary animate-ping" />
                                Always live
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
